/**
 * `formatTimePoint`, `formatWhen` and `formatTick` (1.K-1 deliverable 14): the canonical form of
 * deliverable 12's grammar, which `parse` reads back. No `Intl` and no locale: plain integers
 * without separators, US English ordinals, and the calendar's own names as written. None throws.
 */
import { isTick, PARTS_OF_UNIT, PRECISIONS, QUALIFIERS, TIME_UNITS, } from '@codex/contracts';
import { absoluteYearOf, eraByKey } from '../engine/eras.js';
import { toParts } from '../engine/from-tick.js';
import { floorDiv, mul } from '../engine/math.js';
import { isEmptyParts, isPartsObject } from '../engine/ticks.js';
import { isCompiled, } from '../engine/types.js';
import { monthLength, yearIsLeap } from '../engine/year.js';
import { unitText } from '../parse/grammar.js';
/** 1st, 2nd, 3rd, 4th, 11th, 12th, 13th, 21st, 111th. */
export function ordinal(n) {
    const teen = n % 100 >= 11 && n % 100 <= 13;
    const suffix = teen ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
    return `${n}${suffix}`;
}
function pad(n) {
    return n >= 0 && n < 10 ? `0${n}` : `${n}`;
}
const isInt = (v) => typeof v === 'number' && Number.isSafeInteger(v);
/** The year with its era: the suffix after it, else the name before it, or nothing. */
function yearWithEra(text, era, written) {
    if (!written)
        return text;
    if (era.suffix !== '')
        return `${text} ${era.suffix}`;
    return era.name === '' ? text : `${era.name} ${text}`;
}
function yearText(precision, year) {
    if (precision === 'decade')
        return `${mul(floorDiv(year, 10), 10)}s`;
    if (precision === 'century') {
        const first = mul(floorDiv(year, 100), 100);
        return first < 0 ? `century ${first}` : `${ordinal(first / 100 + 1)} century`;
    }
    return `${year}`;
}
/** One side of a time point in the canonical form, or null when it cannot be written. */
function formatSide(calendar, parts, precision, mode) {
    const part = parts.part;
    if (part !== undefined && !PARTS_OF_UNIT.includes(part))
        return null;
    const lead = part === undefined ? '' : `${part} `;
    if (precision === 'era') {
        const era = typeof parts.era === 'string' ? eraByKey(calendar, parts.era) : undefined;
        const text = era === undefined ? '' : era.name || era.suffix;
        return text === '' ? null : `${lead}${text}`;
    }
    if (parts.era !== undefined && typeof parts.era !== 'string')
        return null;
    const era = parts.era === undefined ? calendar.defaultEra : eraByKey(calendar, parts.era);
    if (era === undefined || !isInt(parts.year))
        return null;
    const written = mode === 'always' || era.key !== calendar.defaultEra.key;
    const year = yearWithEra(yearText(precision, parts.year), era, written);
    if (precision === 'year' || precision === 'decade' || precision === 'century') {
        return `${lead}${year}`;
    }
    if (precision === 'season') {
        const season = calendar.seasons.find((s) => s.key === parts.season);
        return season === undefined || season.name === '' ? null : `${lead}${season.name} ${year}`;
    }
    const month = calendar.months.find((m) => m.intercalary ? m.key === parts.intercalary : m.key === parts.month);
    if ((parts.month !== undefined && parts.intercalary !== undefined) ||
        (month === undefined && (parts.month !== undefined || parts.intercalary !== undefined))) {
        return null;
    }
    if (precision === 'week') {
        if (!isInt(parts.week) || month?.intercalary)
            return null;
        return `${lead}week ${parts.week} of ${month === undefined ? '' : `${month.name} `}${year}`;
    }
    if (month === undefined || month.name === '')
        return null;
    if (precision === 'month')
        return `${lead}${month.name} ${year}`;
    if (parts.day !== undefined && !isInt(parts.day))
        return null;
    if (parts.day === undefined && !month.intercalary)
        return null;
    let day = parts.day === undefined ? '' : `${parts.day} `;
    if (parts.day === undefined) {
        // A festival of several days without a day is its first day: "Tidefeast 1042" would read back
        // as the whole month, so the day is written.
        const abs = absoluteYearOf(era, parts.year);
        if (!Number.isSafeInteger(abs))
            return null;
        if (monthLength(month, yearIsLeap(calendar, abs)) > 1)
            day = '1 ';
    }
    const date = `${lead}${day}${month.name} ${year}`;
    if (precision === 'day')
        return date;
    if (!isInt(parts.hour))
        return null;
    if (precision === 'hour')
        return `${date}, ${pad(parts.hour)}h`;
    return isInt(parts.minute) ? `${date}, ${pad(parts.hour)}:${pad(parts.minute)}` : null;
}
/**
 * The canonical form of a time point's authored columns: `c. ` before, a part before its unit,
 * `?` after, ` – ` between range sides, `until` or `since` for an open side. A side's era is
 * written unless it is the default era. Null without a compiled calendar, for a key the calendar
 * lacks, a side missing a part its precision needs, or an era-precision side on an era with
 * neither name nor suffix; the point need not resolve.
 */
export function formatTimePoint(calendar, input) {
    if (!isCompiled(calendar) || !isPartsObject(input))
        return null;
    const { loParts, hiParts, precision, qualifier } = input;
    if (!PRECISIONS.includes(precision) || !QUALIFIERS.includes(qualifier))
        return null;
    if (!isPartsObject(loParts) || !(hiParts === null || isPartsObject(hiParts)))
        return null;
    const side = (p) => formatSide(calendar, p, precision, 'omitDefault');
    const loOpen = isEmptyParts(loParts);
    const hiOpen = hiParts !== null && isEmptyParts(hiParts);
    let text;
    if (loOpen) {
        const hi = hiParts === null || hiOpen ? null : side(hiParts);
        text = hi === null ? null : `until ${hi}`;
    }
    else {
        const lo = side(loParts);
        const hi = hiParts === null || hiOpen ? '' : side(hiParts);
        text =
            lo === null || hi === null ? null : hiOpen ? `since ${lo}` : hi === '' ? lo : `${lo} – ${hi}`;
    }
    if (text === null)
        return null;
    if (qualifier === 'approximate')
        return `c. ${text}`;
    return qualifier === 'uncertain' ? `${text}?` : text;
}
function anchorText(anchor) {
    const { label, fid } = anchor;
    if (typeof label !== 'string' || label.trim() === '' || label.includes(']'))
        return null;
    if (fid === null)
        return `@[${label}]`;
    if (typeof fid !== 'string' || fid.trim() === '' || fid.includes(')'))
        return null;
    return `@[${label}](ref:${fid})`;
}
const RELATION_WORDS = {
    after: 'after',
    before: 'before',
    during: 'during',
    same_time: 'same time as',
};
/** Every kind of `ParsedWhen` in its canonical form; null for `invalid` and what cannot be written. */
export function formatWhen(parsed, calendar) {
    if (typeof parsed !== 'object' || parsed === null)
        return null;
    switch (parsed.kind) {
        case 'point':
            return formatTimePoint(calendar, parsed);
        case 'undated':
            return 'undated';
        case 'session':
            return isInt(parsed.number) ? `session ${parsed.number}` : null;
        case 'now': {
            const offset = parsed.offset;
            if (offset === null)
                return 'now';
            if (typeof offset !== 'object' || !isInt(offset.n) || offset.n === 0)
                return null;
            if (!TIME_UNITS.includes(offset.unit))
                return null;
            const n = Math.abs(offset.n);
            return `now ${offset.n < 0 ? '-' : '+'} ${n} ${unitText(offset.unit, n)}`;
        }
        case 'relative': {
            const words = Object.hasOwn(RELATION_WORDS, parsed.relation)
                ? RELATION_WORDS[parsed.relation]
                : null;
            const anchor = typeof parsed.anchor === 'object' && parsed.anchor !== null
                ? anchorText(parsed.anchor)
                : null;
            if (words === null || anchor === null)
                return null;
            const lag = parsed.lag;
            if (lag === null)
                return `${words} ${anchor}`;
            if (typeof lag !== 'object' || !TIME_UNITS.includes(lag.unit))
                return null;
            const { min, max, unit } = lag;
            let text;
            if (isInt(min) && isInt(max)) {
                text =
                    min === max ? `${min} ${unitText(unit, min)}` : `${min}–${max} ${unitText(unit, max)}`;
            }
            else if (isInt(min) && max === null) {
                text = `at least ${min} ${unitText(unit, min)}`;
            }
            else if (min === null && isInt(max)) {
                text = `within ${max} ${unitText(unit, max)}`;
            }
            else {
                return null;
            }
            return `${text} ${words} ${anchor}`;
        }
        default:
            return null;
    }
}
/**
 * The display form of a tick for NOW, Chronicle headings and labels, with its era always written
 * (its suffix, else its name): "14 Ember 1042 AL, 20:00". Null as deliverable 2 says, and for a
 * tick that no era contains.
 */
export function formatTick(calendar, tick, precision = 'minute') {
    if (!isCompiled(calendar) || !isTick(tick) || !PRECISIONS.includes(precision))
        return null;
    const parts = toParts(calendar, tick, precision);
    return parts === null ? null : formatSide(calendar, parts, precision, 'always');
}
//# sourceMappingURL=format.js.map